/**
 * A right's life, as an ordered list of dated rows.
 *
 * Deliberately not an SVG. The intervals in a right's life span days to decades; a proportional
 * axis would make most of the chart unreadable in order to show one long gap, and a rail that
 * states its own length in words does not have that problem. Anyone refactoring this towards a
 * chart is undoing the decision.
 *
 * The event kind is in text on every row, not only in the dot's colour.
 */

import type { ReactNode } from 'react';
import { fmtDate } from '~/lib/format';
import styles from './Matter.module.css';

export type ChronologyKind =
  | 'filing'
  | 'registration'
  | 'renewal'
  | 'expiry'
  | 'annuity'
  | 'deadline'
  | 'withheld';

export interface ChronologyEvent {
  id: string;
  date: string;
  kind: ChronologyKind;
  title: string;
  meta?: ReactNode;
  /** Shades the rail after the first term so the renewal cycle reads without measuring. */
  segment?: 'first' | 'renewal';
}

const KIND_LABEL: Record<ChronologyKind, string> = {
  filing: 'Filing',
  registration: 'Registration',
  renewal: 'Renewal',
  expiry: 'Expiry',
  annuity: 'Annuity',
  deadline: 'Deadline',
  withheld: 'Withheld',
};

const DOT_CLASS: Record<ChronologyKind, string | undefined> = {
  filing: undefined,
  registration: styles.dotRegistration,
  renewal: styles.dotRenewal,
  expiry: styles.dotExpiry,
  annuity: styles.dotAnnuity,
  deadline: styles.dotDeadline,
  withheld: styles.dotWithheld,
};

export interface ChronologyProps {
  events: readonly ChronologyEvent[];
  /** Names the list, so a screen reader hears what the rows are before reading them. */
  'aria-label': string;
  /**
   * The elapsed time between two rows, as words. Supplied by the caller from what the server
   * computed — this component never subtracts two dates.
   */
  gapLabel?: (from: ChronologyEvent, to: ChronologyEvent) => string | null;
}

export function Chronology({ events, gapLabel, ...rest }: ChronologyProps) {
  return (
    <ol className={styles.chrono} aria-label={rest['aria-label']}>
      {events.map((event, index) => {
        const next = events[index + 1];
        const gap = next && gapLabel ? gapLabel(event, next) : null;
        return (
          <li key={event.id}>
            <div className={styles.row}>
              <span className={styles.date}>{fmtDate(event.date)}</span>
              <span className={styles.rail}>
                <span
                  className={[styles.dot, DOT_CLASS[event.kind]].filter(Boolean).join(' ')}
                  aria-hidden="true"
                />
              </span>
              <span className={styles.body}>
                <span className={styles.title}>
                  <span className="visually-hidden">{KIND_LABEL[event.kind]}: </span>
                  {event.title}
                </span>
                {event.meta ? <span className={styles.meta}>{event.meta}</span> : null}
              </span>
            </div>
            {next ? (
              <div className={styles.gap} aria-hidden={gap === null || undefined}>
                <span />
                <span className={styles.rail}>
                  <span
                    className={[
                      styles.line,
                      next.segment === 'first' ? styles.lineFirst : undefined,
                      next.segment === 'renewal' ? styles.lineRenewal : undefined,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  />
                </span>
                <span className={styles.duration}>{gap}</span>
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
